import React, { useMemo, useRef, useState, useEffect, useId } from "react";
import { Box, useTheme } from "@mui/material";
import { line, area, curveBasis } from "d3-shape";
import { useCompanyPrice1Min } from "./hooks/useCompanyPrice1Min";

interface MiniChartProps {
  ticker: string;
  width?: number | "responsive";
  /** "responsive" fills the parent's height (measured, like width) — for
   *  charts that should absorb whatever height a stretched card gives them.
   *  The chart is taken out of flow (absolute) so its measured size never
   *  feeds back into the parent's own height; the parent must be
   *  `position: relative` and own its min-height. */
  height?: number | "responsive";
  strokeWidth?: number;
  tradingDate?: string;
  changePercentage?: number; // Actual stock performance for the day
  /**
   * Skip the trading-session filter entirely. Needed for near-24h assets
   * (commodities, futures, crypto) whose bars would otherwise all be dropped
   * outside session hours, leaving a blank chart.
   */
  filterTradingHours?: boolean;
  /**
   * Session for the trading-hours filter, minutes since midnight in the
   * bars' NATIVE exchange wall-clock (FMP stamps intraday bars in local
   * exchange time — ET for US listings, London time for FTSE). Defaults to
   * NYSE 9:30–16:00.
   */
  sessionWindow?: { start: number; end: number };
  /** Poll the intraday data (always-visible surfaces only). Default: no polling. */
  refetchIntervalMs?: number;
}

const MiniChart: React.FC<MiniChartProps> = ({
  ticker,
  width = 100,
  height = 40,
  strokeWidth = 2.5,
  tradingDate,
  changePercentage,
  filterTradingHours = true,
  sessionWindow,
  refetchIntervalMs,
}) => {
  const theme = useTheme();
  // Use unique ID per component instance to prevent SVG gradient ID collisions
  // when multiple charts for the same ticker exist on the page (e.g., tour mode + real app)
  const uniqueId = useId();
  const { data, isLoading, error } = useCompanyPrice1Min(ticker, tradingDate, refetchIntervalMs);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(
    typeof width === "number" ? width : 100
  );
  const [containerHeight, setContainerHeight] = useState<number>(
    typeof height === "number" ? height : 40
  );

  // Measure the container for responsive mode(s)
  useEffect(() => {
    if ((width !== "responsive" && height !== "responsive") || !containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (width === "responsive" && entry.contentRect.width > 0) {
          setContainerWidth(entry.contentRect.width);
        }
        if (height === "responsive" && entry.contentRect.height > 0) {
          setContainerHeight(entry.contentRect.height);
        }
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [width, height]);

  const chartWidth = width === "responsive" ? containerWidth : width;
  const chartHeight = height === "responsive" ? containerHeight : height;

  // Width-INDEPENDENT data processing: parse, filter to trading hours, sort, min/max.
  // Runs once per data fetch — the ResizeObserver width flip never re-runs this.
  const { prices, minPrice, maxPrice } = useMemo(() => {
    if (!data?.data || data.data.length === 0) {
      return { prices: [] as number[], minPrice: 0, maxPrice: 0 };
    }

    // CRITICAL FIX: Sort data chronologically and filter to trading hours
    // This matches the approach in Price1MinChart to ensure correct chart direction.
    // FMP 1-min dates are naive ET wall-clock strings ("YYYY-MM-DD HH:mm:ss"), so the
    // HH:mm digits ARE the New York time — read them directly instead of constructing
    // a dayjs.tz instance (Intl.DateTimeFormat) per bar, and sort on a numeric key
    // derived from the same digits (e.g. 20250829155900).
    const tradingHourData = data.data
      .map((item) => ({
        close: item.close,
        sortKey: Number(item.date.replace(/\D/g, "")),
        timeInMinutes:
          Number(item.date.slice(11, 13)) * 60 + Number(item.date.slice(14, 16)),
      }))
      .filter((item) => {
        if (!filterTradingHours) return true;
        // Session in the bars' native wall-clock; default NYSE 9:30–16:00 ET
        const marketOpen = sessionWindow?.start ?? 9 * 60 + 30;
        const marketClose = sessionWindow?.end ?? 16 * 60;

        return item.timeInMinutes >= marketOpen && item.timeInMinutes <= marketClose;
      })
      .sort((a, b) => a.sortKey - b.sortKey); // Sort chronologically (oldest → newest)

    // Extract prices from sorted and filtered data
    const sortedPrices = tradingHourData.map((record) => record.close);

    // Handle empty data after filtering
    if (sortedPrices.length === 0) {
      return { prices: sortedPrices, minPrice: 0, maxPrice: 0 };
    }

    return {
      prices: sortedPrices,
      minPrice: Math.min(...sortedPrices),
      maxPrice: Math.max(...sortedPrices),
    };
  }, [data, filterTradingHours, sessionWindow?.start, sessionWindow?.end]);

  // Width-DEPENDENT path generation: cheap coordinate mapping + d3 path strings.
  // Only this re-runs when the ResizeObserver reports the measured width.
  const { path, areaPath, color } = useMemo(() => {
    if (prices.length === 0) {
      return { path: "", areaPath: "", color: theme.palette.text.secondary };
    }

    const priceRange = maxPrice - minPrice;

    // Handle flat line case
    if (priceRange === 0) {
      const y = chartHeight / 2;
      const pathString = `M 0 ${y} L ${chartWidth} ${y}`;
      const areaPathString = `M 0 ${chartHeight} L 0 ${y} L ${chartWidth} ${y} L ${chartWidth} ${chartHeight} Z`;
      return { path: pathString, areaPath: areaPathString, color: theme.palette.text.secondary };
    }

    // Create data points array for d3 - sampled for smooth trend curves while
    // keeping enough points that the day's actual texture survives (every 7th
    // rather than 12th; 12 read as over-generalized).
    const samplingInterval = 7;

    // Build sampled array with guaranteed first and last points for accuracy
    const sampledIndices: number[] = [];
    sampledIndices.push(0); // Always include market open

    for (let i = samplingInterval; i < prices.length - 1; i += samplingInterval) {
      sampledIndices.push(i);
    }

    // Always include market close (last point) if not already added
    if (sampledIndices[sampledIndices.length - 1] !== prices.length - 1) {
      sampledIndices.push(prices.length - 1);
    }

    // Map sampled indices to chart coordinates
    const points = sampledIndices.map((originalIndex) => ({
      x: (originalIndex / (prices.length - 1)) * chartWidth,
      y: chartHeight - ((prices[originalIndex] - minPrice) / priceRange) * chartHeight,
    }));

    // Create ultra-smooth line generator with B-spline (curveBasis) for flowing trend visualization
    const lineGenerator = line<{ x: number; y: number }>()
      .x((d) => d.x)
      .y((d) => d.y)
      .curve(curveBasis); // B-spline interpolation creates naturally smooth, flowing curves

    // Create area generator for glow effect below the line
    const areaGenerator = area<{ x: number; y: number }>()
      .x((d) => d.x)
      .y0(chartHeight) // Bottom of chart (baseline)
      .y1((d) => d.y) // Top follows the line
      .curve(curveBasis); // Same smooth curve as line

    // Generate smooth SVG paths
    const pathString = lineGenerator(points) || "";
    const areaPathString = areaGenerator(points) || "";

    // Determine color based on actual day performance if provided, otherwise intraday movement
    let isPositive: boolean;

    if (changePercentage !== undefined) {
      // Use actual stock performance for the day
      isPositive = changePercentage >= 0;
    } else {
      // Fall back to comparing first vs last price in sorted chart data
      // Note: After sorting, first = market open price, last = market close price
      const firstPrice = prices[0]; // Market open (oldest data point)
      const lastPrice = prices[prices.length - 1]; // Market close (newest data point)
      isPositive = lastPrice >= firstPrice;
    }

    const chartColor = isPositive ? "#22c55e" : "#ef4444";

    return { path: pathString, areaPath: areaPathString, color: chartColor };
  }, [prices, minPrice, maxPrice, chartWidth, chartHeight, theme.palette.text.secondary, changePercentage]);

  // Don't render anything if loading, error, or no data
  if (isLoading || error || !data?.data || data.data.length === 0) {
    return null;
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        ...(height === "responsive"
          ? { position: "absolute", inset: 0 }
          : {
              width: width === "responsive" ? "100%" : `${width}px`,
              height: `${height}px`,
            }),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <svg
        width={chartWidth}
        height={chartHeight}
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        preserveAspectRatio="none"
        style={{ display: "block", width: "100%", height: "100%" }}
      >
        {/* Gradient definition for glow effect */}
        <defs>
          <linearGradient id={`gradient-${uniqueId}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Filled area with gradient glow below the line */}
        <path
          d={areaPath}
          fill={`url(#gradient-${uniqueId})`}
          stroke="none"
        />

        {/* Main line path on top */}
        <path
          d={path}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </Box>
  );
};

export default React.memo(MiniChart);
